/**
 * featured-protocol-hero.js
 *
 * Intercepts CTA clicks in the Featured Protocol Hero section so
 * they add to cart via /cart/add.js and open the cart drawer,
 * instead of navigating to the PDP.
 *
 * Wiring (from featured-protocol-hero.liquid):
 *   <a class="fph__cta fph__cta--primary"
 *      href="{{ product.url }}?variant=…&selling_plan=…"
 *      data-fph-atc="sub"
 *      data-fph-variant-id="12345"
 *      data-fph-selling-plan-id="67890">Start the protocol — $X/mo</a>
 *
 *   <a class="fph__cta fph__cta--secondary"
 *      href="{{ product.url }}?variant=…"
 *      data-fph-atc="otp"
 *      data-fph-variant-id="12345">Buy once — $X</a>
 *
 * The href stays as a functional fallback if JS fails or is disabled.
 *
 * Uses document-level capture-phase delegation so it survives
 * DOM mutation (theme editor re-render, Alpine hydration, etc.).
 */
(function () {
  'use strict';

  if (window.__fphAtcInitialized) return;
  window.__fphAtcInitialized = true;

  var IN_FLIGHT = false;

  function fireCartEvents() {
    // The add POST has succeeded. Refresh state, then explicitly open.
    document.dispatchEvent(new CustomEvent('cart:modified'));
    document.dispatchEvent(new CustomEvent('cart:drawer:open'));
  }

  async function addToCart(variantId, sellingPlanId) {
    var item = {
      id: parseInt(variantId, 10),
      quantity: 1
    };
    if (sellingPlanId) {
      item.selling_plan = parseInt(sellingPlanId, 10);
    }

    var response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    });

    if (!response.ok) {
      var errBody;
      try { errBody = await response.json(); } catch (_) { errBody = null; }
      var msg = errBody && errBody.description
        ? errBody.description
        : ('cart/add.js returned ' + response.status);
      throw new Error(msg);
    }

    return response.json();
  }

  function onClick(event) {
    // Composed path so it works even if the click landed on a child span
    var path = event.composedPath ? event.composedPath() : [event.target];
    var cta = null;
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.getAttribute && node.getAttribute('data-fph-atc')) {
        cta = node;
        break;
      }
    }
    if (!cta) return;

    var variantId = cta.getAttribute('data-fph-variant-id');
    if (!variantId) return; // no variant selected in editor → fall through to link nav

    // Middle click / cmd+click / new-tab intent — let the browser handle it
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    event.preventDefault();
    event.stopPropagation();

    if (IN_FLIGHT) return;
    IN_FLIGHT = true;

    var mode = cta.getAttribute('data-fph-atc');
    var sellingPlanId = mode === 'sub' ? cta.getAttribute('data-fph-selling-plan-id') : null;

    // Loading state
    cta.setAttribute('aria-busy', 'true');
    var originalOpacity = cta.style.opacity;
    cta.style.opacity = '0.7';
    cta.style.pointerEvents = 'none';

    addToCart(variantId, sellingPlanId)
      .then(function () {
        fireCartEvents();
      })
      .catch(function (err) {
        console.error('[fph] Add-to-cart failed, navigating to PDP as fallback.', err);
        // Fall back to the anchor's href so the customer still lands on the product
        var href = cta.getAttribute('href');
        if (href && href !== '#') {
          window.location.href = href;
        }
      })
      .finally(function () {
        IN_FLIGHT = false;
        cta.removeAttribute('aria-busy');
        cta.style.opacity = originalOpacity;
        cta.style.pointerEvents = '';
      });
  }

  // Capture phase — beats any bubble-phase interceptors on the section
  document.addEventListener('click', onClick, true);
})();
